import styled from '@emotion/styled';
import { decode } from 'he';
import { forwardRef } from 'react';

import { ChannelLogo, StarRating } from '@travelnest/hui';

import { Text } from '@/components/ui/Text';
import { formatRelativeDate } from '@/lib/dates';
import { MAX_STARS, toStars } from '@/lib/rating';
import { capitalize } from '@/lib/strings';
import { colors, focusRing, font, radii, spacing } from '@/lib/theme';
import { useCurrentLocale, useI18n } from '@/locales';
import type { IReview } from '@/types/content';

const Card = styled.article<{ clickable: boolean }>`
	display: flex;
	flex-direction: column;
	gap: ${spacing.space30};
	padding: ${spacing.space50};
	border-radius: ${radii.radii30};
	background: ${colors.LM_NU10};
	border: 1px solid ${colors.interface.border};
	min-width: 0;

	${({ clickable }) =>
		clickable &&
		`
		cursor: pointer;
		transition: background-color 150ms ease-out;
		&:hover { background: ${colors.LM_NU20}; }
		&:focus-visible { ${focusRing} }
	`}
`;

const Header = styled.header`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: ${spacing.space30};
`;

const Channel = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${spacing.space10};
	${font('IR80')}
	color: ${colors.typography.body};
	white-space: nowrap;
`;

const Body = styled.div<{ clamp: boolean }>`
	display: flex;
	flex-direction: column;
	gap: ${spacing.space20};
	${font('IR100', '160%')}
	color: ${colors.typography.body};

	p {
		margin: 0;
		${({ clamp }) =>
			clamp &&
			`
			display: -webkit-box;
			-webkit-line-clamp: 4;
			-webkit-box-orient: vertical;
			overflow: hidden;
		`}
	}
`;

const Title = styled.h4`
	${font('RWB100', '125%')}
	color: ${colors.typography.heading};
	margin: 0;
`;

export interface ReviewCardProps {
	review: IReview;
	/** Preview cards clamp the text and open the full review on click. */
	preview?: boolean;
	onOpen?: (reviewId: number) => void;
}

export const ReviewCard = forwardRef<HTMLElement, ReviewCardProps>(
	function ReviewCard({ review, preview = false, onOpen }, ref) {
		const t = useI18n();
		const locale = useCurrentLocale();
		const open = () => onOpen?.(review.id);

		return (
			<Card
				ref={ref}
				clickable={preview}
				tabIndex={preview ? 0 : undefined}
				role={preview ? 'button' : undefined}
				onClick={preview ? open : undefined}
				onKeyUp={
					preview ? (e) => e.key === 'Enter' && open() : undefined
				}
				data-testid="review-card"
			>
				<Header>
					<div>
						<Text as="span" color="heading" weight="bold">
							{capitalize(review.reviewerName)}
						</Text>
						<Text variant="caption">
							{formatRelativeDate(review.reviewCreatedAt, locale)}
						</Text>
					</div>
					<Channel>
						{t('property.reviews.verifiedOn')}
						<ChannelLogo name={review.channelId} size={24} />
					</Channel>
				</Header>
				<Body clamp={preview} data-testid="review-card-body">
					{!preview && review.rating > 0 && (
						<StarRating
							total={MAX_STARS}
							filled={toStars(review.rating)}
							size="small"
						/>
					)}
					{review.title && !preview && (
						<Title>{decode(review.title)}</Title>
					)}
					{review.primaryContent && (
						<p>{decode(review.primaryContent)}</p>
					)}
					{!preview && review.secondaryContent && (
						<p>{decode(review.secondaryContent)}</p>
					)}
				</Body>
			</Card>
		);
	},
);
